import { useMemo, useState } from 'react';
import { Anchor, Badge, Select, Text, TextInput } from '@mantine/core';
import { modals } from '@mantine/modals';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { api } from '@/api/client';
import type { AdminUser, Role } from '@/api/types';
import { useMe } from '@/auth/useMe';
import { Page, QueryState } from '@/components/Page';
import { ResponsiveTable, type Column } from '@/components/ResponsiveTable';
import { formatDate } from '@/lib/format';
import { notifyError, notifySuccess } from '@/lib/notify';

const ROLES: Role[] = ['CLIENT', 'COURIER', 'LAUNDRY', 'MANAGER', 'ADMIN'];
const ROLE_COLOR: Partial<Record<Role, string>> = {
    ADMIN: 'red',
    MANAGER: 'orange',
    COURIER: 'blue',
    LAUNDRY: 'grape',
};

const GENDER = { male: '♂', female: '♀' } as const;

export function UsersScreen() {
    const { data: me } = useMe();
    const queryClient = useQueryClient();
    const [query, setQuery] = useState('');

    const users = useQuery({
        queryKey: ['users'],
        queryFn: () => api.get<{ users: AdminUser[] }>('/api/admin/users').then((r) => r.users ?? []),
    });

    const changeRole = useMutation({
        mutationFn: ({ id, role }: { id: number; role: Role }) => api.post(`/api/admin/users/${id}/role`, { role }),
        onSuccess: () => {
            notifySuccess('Роль изменена');
            queryClient.invalidateQueries({ queryKey: ['users'] });
        },
        onError: notifyError,
    });

    const confirmRole = (u: AdminUser, role: Role) =>
        modals.openConfirmModal({
            title: 'Сменить роль?',
            children: (
                <Text size="sm">
                    {u.name || `#${u.id}`}: {u.role || '—'} → <b>{role}</b>
                </Text>
            ),
            labels: { confirm: 'Сменить', cancel: 'Отмена' },
            onConfirm: () => changeRole.mutate({ id: u.id, role }),
        });

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        const list = users.data ?? [];
        if (!q) return list;
        return list.filter((u) =>
            [u.id, u.name, u.username, u.telegram_id, u.role].some((v) => String(v ?? '').toLowerCase().includes(q))
        );
    }, [users.data, query]);

    const columns: Column<AdminUser>[] = [
        {
            key: 'name',
            title: 'Пользователь',
            mobile: 'title',
            render: (u) => u.name || 'Без имени',
        },
        {
            key: 'contact',
            title: 'Telegram',
            mobile: 'subtitle',
            render: (u) =>
                u.username ? (
                    <Anchor href={`https://t.me/${u.username}`} target="_blank" size="sm" onClick={(e) => e.stopPropagation()}>
                        @{u.username}
                    </Anchor>
                ) : (
                    <Text size="sm" c="dimmed" inherit>
                        {u.telegram_id || 'без Telegram'}
                    </Text>
                ),
        },
        { key: 'id', title: 'ID', render: (u) => u.id },
        {
            key: 'role',
            title: 'Роль',
            render: (u) =>
                me?.role === 'ADMIN' && me.id !== u.id ? (
                    <Select
                        size="xs"
                        w={130}
                        data={ROLES}
                        value={u.role ?? null}
                        allowDeselect={false}
                        onChange={(v) => v && v !== u.role && confirmRole(u, v as Role)}
                        onClick={(e) => e.stopPropagation()}
                    />
                ) : (
                    <Badge color={ROLE_COLOR[u.role as Role] ?? 'gray'}>{u.role || '—'}</Badge>
                ),
        },
        {
            key: 'gender',
            title: 'Пол',
            render: (u) =>
                u.gender === 'male' || u.gender === 'female'
                    ? `${GENDER[u.gender]} ${u.gender_probability ? Math.round(u.gender_probability * 100) + '%' : ''}`
                    : '—',
        },
        { key: 'created', title: 'Регистрация', render: (u) => formatDate(u.created_at) },
    ];

    return (
        <Page title="Пользователи">
            <TextInput
                placeholder="Имя, @username, ID или роль"
                leftSection={<Search size={16} />}
                value={query}
                onChange={(e) => setQuery(e.currentTarget.value)}
                maw={420}
            />
            <QueryState isLoading={users.isLoading} error={users.error} onRetry={users.refetch}>
                <Text size="sm" c="dimmed">
                    {filtered.length} из {users.data?.length ?? 0}
                </Text>
                <ResponsiveTable data={filtered} columns={columns} rowKey={(u) => u.id} empty="Никого не нашли" />
            </QueryState>
        </Page>
    );
}
